import { configuredString } from '../config/kioskRuntimeConfig';
import { jsonStorageStore } from '../jsonStorageStore';
import type { PayCardInput } from '../types';
import type { CaptureMode } from './paymentStatus';

// 'capture': the card paid on the reader, and the kiosk accepts the payment
// with the capture, or with the hold step for a capture after print.
export type ActiveIntentStage = 'reader' | 'capture';

export interface PersistedActiveIntent {
  paymentIntentId: string;
  readerId: string;
  idempotencyKey: string;
  sessionId: string;
  stage: ActiveIntentStage;
  amountCents?: number;
  currency?: string;
  checkoutKey?: string;
  paymentDestination?: string | null;
  stripeAccountId?: string | null;
  partnerStripeId?: string | null;
  capture?: CaptureMode;
}

// A cancel releases the held amount only before the capture stage. At the
// capture stage the card paid, and the kiosk accepts that payment also after a
// lost answer of the hold step. A release then cancels the payment of the visitor.
export function releasesHoldOnCancel(intent: {
  capture?: CaptureMode;
  stage?: ActiveIntentStage;
}): boolean {
  return intent.capture === 'after_print' && intent.stage !== 'capture';
}

export interface PaymentAccountContext {
  paymentDestination?: string | null;
  stripeAccountId?: string | null;
  partnerStripeId?: string | null;
}

export interface ActiveIntentStore {
  read(): PersistedActiveIntent | null;
  write(intent: PersistedActiveIntent): void;
  clear(): void;
}

export function createStorageActiveIntentStore(
  storage: Storage | undefined,
  key: string
): ActiveIntentStore {
  return jsonStorageStore<PersistedActiveIntent>(storage, key, (parsed) => {
    if (
      typeof parsed.paymentIntentId !== 'string' ||
      typeof parsed.readerId !== 'string' ||
      typeof parsed.idempotencyKey !== 'string' ||
      typeof parsed.sessionId !== 'string' ||
      (parsed.stage !== undefined &&
        parsed.stage !== 'reader' &&
        parsed.stage !== 'capture')
    ) {
      return null;
    }
    return {
      paymentIntentId: parsed.paymentIntentId,
      readerId: parsed.readerId,
      idempotencyKey: parsed.idempotencyKey,
      sessionId: parsed.sessionId,
      stage: parsed.stage ?? 'reader',
      amountCents:
        typeof parsed.amountCents === 'number' &&
        Number.isFinite(parsed.amountCents)
          ? parsed.amountCents
          : undefined,
      currency:
        typeof parsed.currency === 'string'
          ? parsed.currency.toLowerCase()
          : undefined,
      checkoutKey: configuredString(parsed.checkoutKey) ?? undefined,
      paymentDestination: configuredString(parsed.paymentDestination),
      stripeAccountId: configuredString(parsed.stripeAccountId),
      partnerStripeId: configuredString(parsed.partnerStripeId),
      ...(parsed.capture === 'after_print' || parsed.capture === 'at_payment'
        ? { capture: parsed.capture }
        : {}),
    };
  });
}

export function sameIntent(
  current: PersistedActiveIntent | null,
  intent: PersistedActiveIntent
): boolean {
  return (
    current?.paymentIntentId === intent.paymentIntentId &&
    current.idempotencyKey === intent.idempotencyKey &&
    current.sessionId === intent.sessionId &&
    current.readerId === intent.readerId
  );
}

export function intentMatchesInput(
  intent: PersistedActiveIntent,
  input: PayCardInput
): boolean {
  const checkoutKey = configuredString(input.checkoutKey);
  if (intent.checkoutKey && checkoutKey) {
    return (
      intent.sessionId === input.sessionId &&
      intent.checkoutKey === checkoutKey &&
      intent.amountCents === input.quote.amountDueCents &&
      intent.currency === input.quote.currency.toLowerCase()
    );
  }

  return (
    intent.sessionId === input.sessionId &&
    intent.idempotencyKey === input.idempotencyKey &&
    intent.amountCents === input.quote.amountDueCents &&
    intent.currency === input.quote.currency.toLowerCase()
  );
}

export function withStage(
  intent: PersistedActiveIntent,
  stage: ActiveIntentStage
): PersistedActiveIntent {
  return { ...intent, stage };
}

export function resolvePaymentAccount(
  account: PaymentAccountContext = {}
): Required<PaymentAccountContext> {
  const paymentDestination = configuredString(account.paymentDestination);
  const stripeAccountId = configuredString(account.stripeAccountId);
  const partnerStripeId =
    configuredString(account.partnerStripeId) ??
    (paymentDestination === 'partner' ? stripeAccountId : null);
  return {
    paymentDestination: paymentDestination ?? 'platform',
    stripeAccountId,
    partnerStripeId,
  };
}
